import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  MenuItem,
  CircularProgress,
  Typography,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
} from '@mui/material';
import { z } from 'zod';
import DateField from '../ui/DateField';
import { useFeatureFlag, useSubscriptionLimit, useSubscriptionData } from '../../hooks/useFeatureFlag';

// Schema atualizado para o formato correto da API (Config Pura)
const splitDate = (iso) => (iso ? new Date(iso).toISOString().split('T')[0] : '');
const splitTime = (iso) => (iso ? new Date(iso).toISOString().slice(11, 16) : '');

const ScheduleSchema = z.object({
  professionalId: z.string().min(1, 'Selecione um profissional'),
  placeId: z.string().min(1, 'Selecione um local'),
  name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  description: z.string().optional(),
  maxBookingDaysAhead: z.number().min(1, 'Mínimo 1 dia').max(180, 'Máximo 180 dias'),
  maxAvailableDaysToShow: z.number().min(1, 'Mínimo 1 dia').max(30, 'Máximo 30 dias'),
  minimumAdvanceBookingHours: z.number().min(1, 'Mínimo 1 hora').max(168, 'Máximo 168 horas (7 dias)'),
  startDatePart: z.string().optional().nullable(),
  startTimePart: z.string().optional().nullable(),
  endDatePart: z.string().optional().nullable(),
  endTimePart: z.string().optional().nullable(),
  maxAttendees: z.number().min(1, 'Capacidade deve ser pelo menos 1').max(1000, 'Capacidade máxima de 1000'),
}).refine(
  (data) => {
    if (!data.startDatePart || !data.endDatePart) return true;
    const start = `${data.startDatePart}T${data.startTimePart || '00:00'}`;
    const end   = `${data.endDatePart}T${data.endTimePart || '00:00'}`;
    return end > start;
  },
  { message: 'Data de fim deve ser posterior à data de início', path: ['endDatePart'] }
);

/**
 * Formulário de schedule (Create/Update)
 */
export default function ScheduleForm({ 
  open, 
  onClose, 
  onSubmit, 
  data,
  professionals = [],
  places = [],
  loading = false 
}) {
  // TODO 2.7: Injetar groupSchedulingEnabled, planName, classMaxAttendeesLimit via hooks
  // Chamados no topo do componente (antes de qualquer condição) conforme React Hook Rules.
  // IMPORTANTE: chaves de feature/limit vêm em snake_case do backend (não há
  // DictionaryKeyPolicy configurada em Program.cs). Ver useFeatureFlag.js.
  const groupSchedulingEnabled = useFeatureFlag('group_scheduling');
  const { planName } = useSubscriptionData();  // planName vem de data.plan.name
  const classMaxAttendeesLimit = useSubscriptionLimit('class_max_attendees');

  const isEdit = !!data;

  // Buscar nomes do profissional e local para exibição no modo edição
  const professionalName = useMemo(() => {
    if (!data) return '';
    // Formato BFF normalizado: professionalId direto no schedule
    const profId = data.professionalId;
    if (profId) {
      const professional = professionals.find(p => p.id === profId);
      if (professional?.name) return professional.name;
    }
    // Fallback legado
    if (data.placeProfessional?.professional?.name) return data.placeProfessional.professional.name;
    return '';
  }, [data, professionals]);

  const placeName = useMemo(() => {
    if (!data) return '';
    // Formato BFF normalizado: placeId direto no schedule
    const placeId = data.placeId;
    if (placeId) {
      const place = places.find(p => p.id === placeId);
      if (place?.name) return place.name;
    }
    // Fallback legado
    if (data.placeProfessional?.place?.name) return data.placeProfessional.place.name;
    return '';
  }, [data, places]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(ScheduleSchema),
    mode: 'onChange',
    defaultValues: {
      professionalId: '',
      placeId: '',
      name: '',
      description: '',
      maxBookingDaysAhead: 30,
      maxAvailableDaysToShow: 7,
      minimumAdvanceBookingHours: 1,
      startDatePart: '',
      startTimePart: '',
      endDatePart: '',
      endTimePart: '',
      maxAttendees: 1,
    },
  });

  // Watch maxAttendees para detectar redução de capacidade
  const currentMaxAttendees = watch('maxAttendees');
  const previousMaxAttendees = data?.maxAttendees || 1;

  // TODO 2.6: Detectar se está reduzindo capacidade em modo edit
  const isCapacityDecreasing = useMemo(() => {
    return isEdit && currentMaxAttendees < previousMaxAttendees && previousMaxAttendees > 1;
  }, [isEdit, currentMaxAttendees, previousMaxAttendees]);

  // Watch selected professional to filter places
  const selectedProfessionalId = watch('professionalId');

  // Filter places based on selected professional
  const filteredPlaces = useMemo(() => {
    if (!selectedProfessionalId) return places;
    return places.filter(place => 
      place.professionalIds?.includes(selectedProfessionalId)
    );
  }, [places, selectedProfessionalId]);

  // Reset form when data changes
  useEffect(() => {
    if (open) {
      if (data) {
        // Edit mode: populate with existing schedule data
        reset({
          professionalId: data.professionalId?.toString() || data.placeProfessional?.professionalId?.toString() || '',
          placeId: data.placeId?.toString() || data.placeProfessional?.placeId?.toString() || '',
          name: data.name || '',
          description: data.description || '',
          maxBookingDaysAhead: data.maxBookingDaysAhead || 30,
          maxAvailableDaysToShow: data.maxAvailableDaysToShow || 7,
          minimumAdvanceBookingHours: data.minimumAdvanceBookingHours || 1,
          startDatePart: splitDate(data.startDate),
          startTimePart: splitTime(data.startDate),
          endDatePart: splitDate(data.endDate),
          endTimePart: splitTime(data.endDate),
          maxAttendees: data.maxAttendees || 1,
        });
      } else {
        // Create mode: reset to defaults
        reset({
          professionalId: '',
          placeId: '',
          name: '',
          description: '',
          maxBookingDaysAhead: 30,
          maxAvailableDaysToShow: 7,
          minimumAdvanceBookingHours: 1,
          startDatePart: '',
          startTimePart: '',
          endDatePart: '',
          endTimePart: '',
          maxAttendees: 1,
        });
      }
    }
  }, [data, open, reset]);

  const onSubmitForm = (formData) => {
    const combineToIso = (date, time) => {
      if (!date) return null;
      return `${date}T${time || '00:00'}:00.000Z`;
    };
    const apiData = {
      professionalId: formData.professionalId,
      placeId: formData.placeId,
      name: formData.name,
      description: formData.description || '',
      maxBookingDaysAhead: Number(formData.maxBookingDaysAhead),
      maxAvailableDaysToShow: Number(formData.maxAvailableDaysToShow),
      minimumAdvanceBookingHours: Number(formData.minimumAdvanceBookingHours),
      startDate: combineToIso(formData.startDatePart, formData.startTimePart),
      endDate: combineToIso(formData.endDatePart, formData.endTimePart),
      maxAttendees: Number(formData.maxAttendees) || 1,
    };
    onSubmit(apiData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'Editar Agenda' : 'Nova Agenda'}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Profissional */}
            {isEdit ? (
              <Box sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                  Profissional
                </Typography>
                <Typography variant="body1">👨‍⚕️ {professionalName}</Typography>
              </Box>
            ) : (
              <TextField
                select
                label="Profissional"
                fullWidth
                {...register('professionalId')}
                error={!!errors.professionalId}
                helperText={errors.professionalId?.message}
                required
                disabled={loading}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">Selecione um profissional</option>
                {professionals.map((prof) => (
                  <option key={prof.id} value={prof.id}>
                    {prof.name}
                  </option>
                ))}
              </TextField>
            )}

            {/* Local */}
            {isEdit ? (
              <Box sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                  Local
                </Typography>
                <Typography variant="body1">📍 {placeName}</Typography>
              </Box>
            ) : (
              <TextField
                select
                label="Local"
                fullWidth
                {...register('placeId')}
                error={!!errors.placeId}
                helperText={errors.placeId?.message}
                required
                disabled={loading || !selectedProfessionalId}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">
                  {!selectedProfessionalId 
                    ? 'Selecione um profissional primeiro' 
                    : filteredPlaces.length === 0 
                      ? 'Nenhum local disponível para este profissional'
                      : 'Selecione um local'}
                </option>
                {filteredPlaces.map((place) => (
                  <option key={place.id} value={place.id}>
                    {place.name}
                  </option>
                ))}
              </TextField>
            )}

            {/* Nome da agenda */}
            <TextField
              label="Nome da Agenda"
              fullWidth
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              required
              disabled={loading}
              placeholder="Ex: Agenda Manhã - Consultório Centro"
            />

            {/* Descrição */}
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={3}
              {...register('description')}
              error={!!errors.description}
              helperText={errors.description?.message}
              disabled={loading}
              placeholder="Descrição opcional da agenda"
            />

            {/* TODO 2.6: Tipo de Atendimento (Individual/Turma) */}
            {groupSchedulingEnabled && (
              <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  Tipo de Atendimento
                </Typography>
                <RadioGroup
                  value={currentMaxAttendees > 1 ? 'class' : 'individual'}
                  onChange={(e) => {
                    const isClass = e.target.value === 'class';
                    const newValue = isClass ? Math.max(2, currentMaxAttendees || 10) : 1;
                    setValue('maxAttendees', newValue);
                  }}
                >
                  <FormControlLabel
                    value="individual"
                    control={<Radio />}
                    label="Individual (1 cliente por horário)"
                  />
                  <FormControlLabel
                    value="class"
                    control={<Radio />}
                    label="Turma / Grupo (vários clientes no mesmo horário)"
                  />
                </RadioGroup>

                {currentMaxAttendees > 1 && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      type="number"
                      label="Capacidade máxima"
                      fullWidth
                      {...register('maxAttendees', { valueAsNumber: true })}
                      error={!!errors.maxAttendees}
                      helperText={
                        errors.maxAttendees?.message ||
                        (planName && classMaxAttendeesLimit
                          ? `Plano ${planName}: até ${classMaxAttendeesLimit} participantes.`
                          : 'Capacidade máxima de participantes por slot')
                      }
                      inputProps={{ min: 2, max: classMaxAttendeesLimit || 1000 }}
                      disabled={loading}
                    />

                    {isCapacityDecreasing && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        <strong>Atenção:</strong> Você está reduzindo a capacidade. Agendamentos futuros com ocupação
                        superior à nova capacidade poderão ser bloqueados.
                      </Alert>
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {/* Máximo de Dias para Agendamento */}
            <TextField
              label="Máximo de Dias para Agendamento"
              type="number"
              fullWidth
              {...register('maxBookingDaysAhead', { valueAsNumber: true })}
              error={!!errors.maxBookingDaysAhead}
              helperText={errors.maxBookingDaysAhead?.message || 'Quantos dias à frente os clientes podem agendar (1-180 dias)'}
              required
              disabled={loading}
              inputProps={{ min: 1, max: 180 }}
            />

            {/* Dias Disponíveis a Mostrar */}
            <TextField
              label="Dias Disponíveis a Mostrar"
              type="number"
              fullWidth
              {...register('maxAvailableDaysToShow', { valueAsNumber: true })}
              error={!!errors.maxAvailableDaysToShow}
              helperText={errors.maxAvailableDaysToShow?.message || 'Quantos dias com disponibilidade mostrar no frontend (1-30 dias)'}
              required
              disabled={loading}
              inputProps={{ min: 1, max: 30 }}
            />

            {/* Horas Mínimas de Antecedência */}
            <TextField
              label="Horas Mínimas de Antecedência"
              type="number"
              fullWidth
              {...register('minimumAdvanceBookingHours', { valueAsNumber: true })}
              error={!!errors.minimumAdvanceBookingHours}
              helperText={errors.minimumAdvanceBookingHours?.message || 'Quantas horas de antecedência necessárias para agendamento (1-168 horas)'}
              required
              disabled={loading}
              inputProps={{ min: 1, max: 168 }}
            />

            {/* Datas de Vigência */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Vigência (opcional)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name="startDatePart"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        label="Data de Início"
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={!!errors.startDatePart}
                        helperText={errors.startDatePart?.message || 'Início da vigência (opcional)'}
                        disabled={loading}
                      />
                    )}
                  />
                </Box>
                <TextField
                  label="Hora"
                  type="time"
                  sx={{ width: 140, flexShrink: 0 }}
                  {...register('startTimePart')}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'pt-BR' }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Controller
                    name="endDatePart"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        label="Data de Fim"
                        value={field.value || ''}
                        onChange={field.onChange}
                        error={!!errors.endDatePart}
                        helperText={errors.endDatePart?.message || 'Fim da vigência (opcional)'}
                        disabled={loading}
                      />
                    )}
                  />
                </Box>
                <TextField
                  label="Hora"
                  type="time"
                  sx={{ width: 140, flexShrink: 0 }}
                  {...register('endTimePart')}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'pt-BR' }}
                />
              </Box>
            </Box>

            {/* Info sobre Turnos */}
            <Alert severity="info">
              <strong>Atenção:</strong> Após criar a agenda, você poderá adicionar os turnos (horários específicos) na tela de gerenciamento.
            </Alert>

            {/* Alert de validação */}
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                Por favor, corrija os erros acima antes de salvar.
              </Alert>
            )}

            {/* Informação */}
            <Alert severity="info" sx={{ mt: 1 }}>
              Esta agenda define o horário de atendimento do profissional no local selecionado 
              para o dia da semana escolhido.
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading || !isValid || (!isDirty && !isEdit)}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
